/**
 * Branch Setting Service
 * API client for branch settings management
 */

import api from '@/lib/axios'
import { branchSettingsConstants } from '@/config/api/branch/branch-settings.constants'
import { tokenManager } from '@/utils/tokenManager'
import { decodeJWT } from '@/utils/permissionUtils'
import type {
  Setting,
  SettingCategory,
  BulkUpdateSettingsInput,
  GetSettingsParams,
} from '@/types/platform/setting.types'

class BranchSettingService {
  /**
   * Get organization and branch slugs from context token
   */
  private getOrgAndBranchSlugs(): { orgSlug: string; branchSlug: string } {
    const contextToken = tokenManager.getToken('contextToken')
    if (!contextToken) {
      throw new Error('Context token not found')
    }

    const payload = decodeJWT(contextToken)
    if (!payload) {
      throw new Error('Failed to decode context token')
    }

    const orgSlug = payload.organizationSlug || (payload as any).orgSlug
    const branchSlug = payload.branchSlug || (payload as any).branchSlug

    if (!orgSlug || !branchSlug) {
      throw new Error('Organization or branch slug not found in token')
    }

    return { orgSlug, branchSlug }
  }

  /**
   * Get branch settings (only branch-scoped, no inherited settings)
   * Shows only settings assigned to the branch at registration time
   */
  async getSettings(params: GetSettingsParams = {}): Promise<Setting[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    const queryParams = new URLSearchParams()
    
    if (params.category) {
      queryParams.append('category', params.category)
    }
    
    // Only show branch-scoped settings, exclude inherited platform/organization settings
    queryParams.append('includeInherited', 'false')
    
    // Don't group by category - return flat array
    queryParams.append('groupByCategory', 'false')

    const url = `${branchSettingsConstants.SETTINGS.GET(orgSlug, branchSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    // Handle both flat array and grouped response
    if (Array.isArray(data.settings)) {
      return data.settings
    }
    
    if (data.groupedByCategory) {
      // Flatten grouped response
      return Object.values(data.groupedByCategory).flat() as Setting[]
    }
    
    // If data is directly an array
    if (Array.isArray(data)) {
      return data
    }
    
    return []
  }

  /**
   * Get branch settings with inheritance (includes platform and organization settings)
   * Used for displaying effective settings with full inheritance chain
   */
  async getSettingsWithInheritance(params: GetSettingsParams = {}): Promise<Setting[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    const queryParams = new URLSearchParams()
    
    if (params.category) {
      queryParams.append('category', params.category)
    }
    
    // Include inherited settings from platform and organization
    queryParams.append('includeInherited', 'true')
    
    // Don't group by category - return flat array
    queryParams.append('groupByCategory', 'false')

    const url = `${branchSettingsConstants.SETTINGS.GET(orgSlug, branchSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    // Handle both flat array and grouped response
    if (Array.isArray(data.settings)) {
      return data.settings
    }
    
    if (data.groupedByCategory) {
      // Flatten grouped response
      return Object.values(data.groupedByCategory).flat() as Setting[]
    }
    
    // If data is directly an array
    if (Array.isArray(data)) {
      return data
    }
    
    return []
  }

  /**
   * Get categories for branch settings
   */
  async getCategories(params: GetSettingsParams = {}): Promise<SettingCategory[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    const queryParams = new URLSearchParams()
    
    // Get grouped response to extract categories
    queryParams.append('groupByCategory', 'true')
    // Only show branch-scoped settings, exclude inherited settings
    queryParams.append('includeInherited', 'false')
    
    if (params.category) {
      queryParams.append('category', params.category)
    }

    const url = `${branchSettingsConstants.SETTINGS.GET(orgSlug, branchSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    if (data.groupedByCategory) {
      return Object.keys(data.groupedByCategory).map((categoryKey) => {
        const categorySettings = data.groupedByCategory[categoryKey] || []
        return {
          name: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          key: categoryKey,
          count: categorySettings.length,
        }
      })
    }
    
    // Fallback: extract categories from flat array
    if (Array.isArray(data.settings)) {
      const uniqueCategories = Array.from(new Set(data.settings.map((s: Setting) => s.category))) as string[]
      return uniqueCategories.map((cat: string) => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/([A-Z])/g, ' $1').trim(),
        key: cat,
        count: data.settings.filter((s: Setting) => s.category === cat).length,
      }))
    }
    
    // If data is directly an array
    if (Array.isArray(data)) {
      const uniqueCategories = Array.from(new Set(data.map((s: Setting) => s.category))) as string[]
      return uniqueCategories.map((cat: string) => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/([A-Z])/g, ' $1').trim(),
        key: cat,
        count: data.filter((s: Setting) => s.category === cat).length,
      }))
    }
    
    return []
  }

  /**
   * Bulk update branch settings
   */
  async bulkUpdateSettings(input: BulkUpdateSettingsInput): Promise<Setting[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    
    // Transform updates to backend format
    const settings = input.updates.map((update) => ({
      id: update.id,
      value: update.value,
    }))

    const response = await api.put(branchSettingsConstants.SETTINGS.UPDATE(orgSlug, branchSlug), {
      settings,
    })
    
    return response.data?.data?.settings || response.data?.data || []
  }

  /**
   * Get current user's settings with inheritance (platform -> organization -> branch -> user).
   * Uses userId from token. Optional: includeInherited=false to get only user-overridden values.
   */
  async getUserSettings(params: GetSettingsParams & { groupByCategory?: boolean; includeInherited?: boolean } = {}): Promise<Setting[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    queryParams.append('groupByCategory', params.groupByCategory !== false ? 'false' : 'true')
    if (params.includeInherited === false) queryParams.append('includeInherited', 'false')

    const url = `${branchSettingsConstants.USER_SETTINGS.GET(orgSlug, branchSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    const data = response.data?.data || response.data
    if (Array.isArray(data.settings)) return data.settings
    if (data.groupedByCategory) return Object.values(data.groupedByCategory).flat() as Setting[]
    return []
  }

  /**
   * Get user settings categories (from user settings response grouped by category).
   */
  async getUserSettingsCategories(): Promise<SettingCategory[]> {
    const settings = await this.getUserSettings({ groupByCategory: false })
    const byCategory = settings.reduce((acc: Record<string, Setting[]>, s) => {
      const cat = s.category || 'general'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(s)
      return acc
    }, {})
    return Object.entries(byCategory).map(([key, list]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
      key,
      count: list.length,
    }))
  }

  /**
   * Update current user's settings. Payload: array of { id or key, value }. UserId from token.
   */
  async updateUserSettings(updates: Array<{ id?: string; key?: string; value: any }>): Promise<Setting[]> {
    const { orgSlug, branchSlug } = this.getOrgAndBranchSlugs()
    const settings = updates.map((u) => ({ id: u.id, key: u.key, value: u.value }))
    const response = await api.put(branchSettingsConstants.USER_SETTINGS.UPDATE(orgSlug, branchSlug), { settings })
    return response.data?.data?.settings || response.data?.data || []
  }
}

export const branchSettingService = new BranchSettingService()
export default branchSettingService

